import { $getRoot, $getSelection, $isRangeSelection, $isRootOrShadowRoot } from 'lexical'
import type { BlockType } from '@/components/editor/lexicalPlugins/suggestions/blockTypeSuggestionUtils'
import { $getElementBlockType, blockTypeToCreateElementFn } from '@/components/editor/lexicalPlugins/suggestions/blockTypeSuggestionUtils'
import { randomId } from '@/lib/random'
import { $findMatchingParent, $insertFirst } from '@lexical/utils'
import type { ProtonNode } from './ProtonNode'
import { $createSuggestionNode, $isSuggestionNode } from './ProtonNode'
import type { Logger } from '@/lib/vendor/proton/logger'
import { $isListItemNode } from '@lexical/list'
import type { ListInfo } from '@/components/editor/lexicalPlugins/suggestions/$getListInfo'
import { $getListInfo } from '@/components/editor/lexicalPlugins/suggestions/$getListInfo'
import { $isNonInlineLeafElement } from '@/lib/vendor/proton/isNonInlineLeafElement'
import type { BlockTypeChangeSuggestionProperties } from './Types'
import { $removeSuggestionNodeAndResolveIfNeeded } from './removeSuggestionNodeAndResolveIfNeeded'
import { $isEmptyListItemExceptForSuggestions } from './Utils'
import { type ContainerQuoteNode, $isContainerQuoteNode, $wrapInQuote, $unwrapQuote } from '@/components/editor/lexicalPlugins/quote/ContainerQuoteNode'

/**
 * Handles quote wrapping as a suggestion. Wraps the selected block(s) in a
 * ContainerQuoteNode and inserts a 'quote-wrap' suggestion marker.
 *
 * Exported so the markdown shortcut handler can always wrap (rather than
 * toggle, which is what $setBlocksTypeAsSuggestion does).
 */
export function $wrapInQuoteAsSuggestion(
  onSuggestionCreation: (id: string) => void,
  logger: Logger,
): boolean {
  const selection = $getSelection()
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return true
  }

  const anchorNode = selection.anchor.getNode()
  // Find the top-level block (child of root or shadow root)
  const block = $findMatchingParent(anchorNode, (n) => {
    const parent = n.getParent()
    return parent !== null && $isRootOrShadowRoot(parent)
  })
  if (!block) {
    logger.info('Could not find top-level block for quote wrap')
    return true
  }

  const suggestionID = randomId()
  $wrapInQuote([block])

  // Place suggestion marker inside the first leaf block inside the quote
  const firstLeaf = $findMatchingParent(anchorNode, $isNonInlineLeafElement)
  if (firstLeaf) {
    const suggestionNode = $createSuggestionNode(suggestionID, 'quote-wrap', {})
    $insertFirst(firstLeaf, suggestionNode)
  }

  onSuggestionCreation(suggestionID)
  return true
}

/**
 * Handles quote unwrapping as a suggestion. Moves all children out of the
 * ContainerQuoteNode and inserts a 'quote-unwrap' suggestion marker.
 */
function $unwrapFromQuoteAsSuggestion(
  quoteNode: ContainerQuoteNode,
  onSuggestionCreation: (id: string) => void,
  logger: Logger,
): boolean {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return true
  }

  const suggestionID = randomId()
  $unwrapQuote(quoteNode)

  // Place suggestion marker inside the first moved-out block
  const anchorNode = selection.anchor.getNode()
  const firstLeaf = $findMatchingParent(anchorNode, $isNonInlineLeafElement)
  if (firstLeaf) {
    const suggestionNode = $createSuggestionNode(suggestionID, 'quote-unwrap', {})
    $insertFirst(firstLeaf, suggestionNode)
  }

  onSuggestionCreation(suggestionID)
  return true
}

export function $setBlocksTypeAsSuggestion(
  blockType: BlockType,
  onSuggestionCreation: (id: string) => void,
  logger: Logger,
): boolean {
  logger.info('Setting block(s) type for selection', blockType)

  const selection = $getSelection()
  if (selection === null) {
    logger.info('No selection found to set blocks type')
    return true
  }

  // Special handling for quote: wrap/unwrap instead of block-type replacement
  if (blockType === 'quote') {
    if (!$isRangeSelection(selection)) {
      return true
    }
    const anchorNode = selection.anchor.getNode()
    const existingQuote = $findMatchingParent(anchorNode, $isContainerQuoteNode)
    if (existingQuote) {
      return $unwrapFromQuoteAsSuggestion(existingQuote, onSuggestionCreation, logger)
    }
    return $wrapInQuoteAsSuggestion(onSuggestionCreation, logger)
  }

  if (blockType === 'code') {
    logger.info('Bailing as code block suggestions are not supported')
    return true
  }

  const createElement = blockTypeToCreateElementFn[blockType]

  const anchorAndFocus = selection.getStartEndPoints()
  const anchor = anchorAndFocus ? anchorAndFocus[0] : null

  const suggestionID = randomId()

  if (anchor !== null && anchor.key === 'root') {
    logger.info('Anchor is root node')

    const element = createElement()
    const root = $getRoot()
    const firstChild = root.getFirstChild()

    if (firstChild) {
      firstChild.replace(element, true)
    } else {
      root.append(element)
    }

    $insertFirst(
      element,
      $createSuggestionNode(suggestionID, 'block-type-change', {
        initialBlockType: 'paragraph',
        targetBlockType: blockType,
      }),
    )

    onSuggestionCreation(suggestionID)

    return true
  }

  const nodes = selection.getNodes()
  const firstSelectedBlock = anchor !== null ? $findMatchingParent(anchor.getNode(), $isNonInlineLeafElement) : false
  if (firstSelectedBlock && nodes.indexOf(firstSelectedBlock) === -1) {
    nodes.push(firstSelectedBlock)
  }

  let didCreateSuggestion = false

  for (const node of nodes) {
    if (!$isNonInlineLeafElement(node)) {
      logger.info(`Skipping node of type ${node.__type} as its not a block-level element`)
      continue
    }

    const nodeBlockType = $getElementBlockType(node)
    if (!nodeBlockType) {
      logger.info(`Skipping node because changing its block type is not yet supported`)
      continue
    }

    let listInfo: ListInfo | undefined
    if ($isListItemNode(node)) {
      listInfo = $getListInfo(node)
      if (!listInfo) {
        continue
      }
    }

    const initialFormatType = node.getFormatType()
    const initialIndent = node.getIndent()

    const targetElement = createElement()
    targetElement.setFormat(initialFormatType)
    targetElement.setIndent(initialIndent)
    node.replace(targetElement, true)

    if ($isRangeSelection(selection) && selection.isCollapsed() && $isEmptyListItemExceptForSuggestions(node)) {
      targetElement.selectEnd()
    }

    const existingSuggestion = targetElement
      .getChildren()
      .find(
        (node): node is ProtonNode =>
          $isSuggestionNode(node) && node.getSuggestionTypeOrThrow() === 'block-type-change',
      )
    if (existingSuggestion) {
      const initialBlockType =
        existingSuggestion.getSuggestionChangedProperties<BlockTypeChangeSuggestionProperties>()?.initialBlockType
      if (!initialBlockType) {
        throw new Error("Existing block-type-change suggestion doesn't have initialBlockType")
      }
      if (initialBlockType === blockType) {
        $removeSuggestionNodeAndResolveIfNeeded(existingSuggestion)
      }
    } else {
      const properties = {
        initialBlockType: nodeBlockType,
        targetBlockType: blockType,
        initialFormatType,
        initialIndent,
        listInfo,
      } satisfies BlockTypeChangeSuggestionProperties
      const suggestionNode = $createSuggestionNode(suggestionID, 'block-type-change', properties)
      $insertFirst(targetElement, suggestionNode)
    }

    didCreateSuggestion = true
  }

  if (didCreateSuggestion) {
    onSuggestionCreation(suggestionID)
  }

  return true
}
